import { INotificationProperties, Notification } from '@vfde-brix/ws10/notification';
import { CLASSNAME_HIDDEN } from '@vfde-brix/ws10/styles';
import * as mapDispatch from '../action';
import { mountResultNotification } from '../../../components/Notification';
import { getCMSConfig } from '../../../helpers/windowObject.helper';
import { showForm } from './form.helper';
import { debugInfo } from '../../../utils/debug';
import { IErrorDescription, IErrorNotification, SalesLeadErrorResponse } from '../interface';
import { CaptchaMediaType, DXLErrorCode } from '../enum';
import { AppId } from '../../../enums/elementId';
import { initialState } from '../reducer';

/* istanbul ignore next */
const getNotificationDescription = (errorCode: DXLErrorCode | undefined, errorDesc: IErrorDescription) => {
    switch (errorCode) {
        case DXLErrorCode.MISSING_ONE_MANDATORY_FIELD:
        case DXLErrorCode.MISSING_MANDATORY_FIELD:
        case DXLErrorCode.METHOD_ARGUMENT_NOT_VALID:
        case DXLErrorCode.NO_CONTACT_ADDED:
        case DXLErrorCode.INVALID_FIELD_VALUE:
        case DXLErrorCode.BAD_REQUEST:
            return errorDesc.missingInput;
        case DXLErrorCode.BAD_REQUEST_CAPTCHA:
            return errorDesc.captchaInvalid;
        case DXLErrorCode.INVLAID_PHONE_NUMBER:
            return errorDesc.phoneNumberInvalid;
        default:
            return errorDesc.general;
    }
};

/* istanbul ignore next */
const getSuccessProperties = (): INotificationProperties => {
    const {
        stdHeadline,
        txtContent,
        buttonText,
        redirectUrl,
    } = getCMSConfig().resultNotification.success;

    return {
        optState: 'success',
        optBackground: 'white',
        stdHeadline,
        txtContent,
        business: {},
        containerButtons: [{
            optSize: 'standard',
            optColor: 'primary',
            stdContent: buttonText,
            optWidthVariation: 'hybrid',
            optDisabled: false,
            business: {},
            optInverse: false,
            linkHref: redirectUrl,
            type: 'Button',
        }],
    };
};

/* istanbul ignore next */
const getErrorProperties = (
    error?: SalesLeadErrorResponse,
    action?: typeof mapDispatch
): INotificationProperties => {
    const {
        stdHeadline,
        buttonText,
        description,
    }: IErrorNotification = getCMSConfig().resultNotification.error;

    return {
        optState: 'error',
        optBackground: 'white',
        stdHeadline,
        txtContent: error
            ? getNotificationDescription(error?.code, description)
            : '',
        business: {},
        containerButtons: [{
            optSize: 'standard',
            optColor: 'tertiary',
            stdContent: buttonText,
            optWidthVariation: 'hybrid',
            optDisabled: false,
            optInverse: false,
            icon: {
                optPosition: 'right',
                stdIconName: 'refresh',
            },
            business: {
                onClick: () => {
                    // Note: Generate new captcha for every try again
                    action?.initStore(initialState);
                    action?.getCaptcha(CaptchaMediaType.HEADER_IMAGE);
                    showForm();
                },
            },
            type: 'Button',
        }],
    };
};

/* istanbul ignore next */
/** Shows result notification based on form submit result */
export const showResults = (
    formContainer: HTMLDivElement | HTMLElement | null,
    resultNotification: Notification | undefined,
    isSuccess: boolean,
    action?: typeof mapDispatch,
    error?: SalesLeadErrorResponse
) => {
    if (!formContainer) {
        debugInfo('Form container not found!');
        return;
    }

    if (!resultNotification) {
        debugInfo('Result notification not found!');
        return;
    }

    mountResultNotification(isSuccess
        ? getSuccessProperties()
        : getErrorProperties(error, action)
    );

    const resultNotificationContainer = resultNotification.getContainerElement();

    // hide form
    formContainer.classList.add(CLASSNAME_HIDDEN);
    // show notification
    resultNotificationContainer.classList.remove(CLASSNAME_HIDDEN);
    // scroll to notification
    resultNotificationContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
};

/* istanbul ignore next */
/** Shows or hides form validation warn notification */
export const setValidationErrorVisibility = (visible: boolean) => {
    const validationNotification = document.getElementById(AppId.ID_WRAPPER_NOTIFICATION_VALIDATION_ERROR);

    if (!validationNotification) {
        return;
    }

    // show notification
    validationNotification.classList.toggle(CLASSNAME_HIDDEN, !visible);
    // scroll to notification
    validationNotification.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
};
